import { Box, Checkbox, Flex, Text } from '@chakra-ui/react'

import type { OfferPackage, Variant } from 'BrokerTsLib/src/dtos'
import { PackageItemTypeCategory, Risk } from 'BrokerTsLib/src/enums'
import _ from 'lodash'
import { useContext } from 'react'
import { LoadingOverlayContext } from '../../../components/LoadingOverlay'
import { useOfferGrid } from '../../../context/OfferGridContext'
import type { IOffersContext } from '../../../context/OffersProvider'
import { useGetOffersContext } from '../../../context/useGetOffersContext'
import { useGetGroupedRisks } from '../../../hooks/useEnumProperty'
import { useResolveChangedParts } from '../../../hooks/useOffer'
import { useInsuranceParamsContext } from '../../../layouts/Wizard'
import { OfferPackageColumnSection } from './OfferPackageColumnSection'
import { GetPackageItemLimit, changePartLimit } from './components/OfferCell'
import { SectionTitle } from './components/SectionTitle'
import type { icons } from './icons'

export const excludeRisksInGrid = [
  Risk.PROPERTY_TYPE,
  Risk.RANGE_TYPE
]

export const OfferPropertyHeaderColumn = ({ variant }: { variant: Variant }) => {
  const { insuranceParams } = useInsuranceParamsContext()
  const { openedColumns, onSectionTitleClick, risks } = useOfferGrid()
  const insuranceGroups = useGetGroupedRisks(variant, excludeRisksInGrid)
  const { setLoading } = useContext(LoadingOverlayContext)
  const { mutateAsync: resolveParts } = useResolveChangedParts()

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const responsibility = insuranceGroups['0']?.find(x => x.id == Risk.RESPONSIBILITY)
  const responsibilityRealestate = insuranceGroups['0']?.find(x => x.id == Risk.RESPONSIBILITY_REALESTATE)
  const spolProperty = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_PROPERTY)
  const spolFlood = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_FLOOD)
  const spolResponsibility = insuranceGroups['0']?.find(x => x.id == Risk.SPOL_RESPONSIBILITY)

  const responsibilityRiskItem = risks.find(x => x.riskId == Risk.RESPONSIBILITY)
  const responsibilityRealestateRiskItem = risks.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)


  const { updateOffer, offers } = useGetOffersContext() as IOffersContext<OfferPackage>
  const handleToggleAll = (show: boolean, riskId: number) => {
    offers?.forEach(o => {
      const offer = _.cloneDeep(o)

      const parts = offer.parts.filter(x => x.riskId == riskId) ?? []
      if (parts.length == 0) return

      let changed = false
      parts.forEach(part => {
        if (!part.packageItem?.addable)
          return

        const [value, packageItemVariantId] = GetPackageItemLimit(part, part.packageItem)
        changed = changePartLimit(part, show, value as number, packageItemVariantId as number) || changed
      })

      if (!changed)
        return

      setLoading(true)

      resolveParts({
        offer: offer,
        packageItemsIds: parts.map(x => x.packageItem!.packageItemId!) ?? []
      })
        .then(({ data: updatedOffer }) => {
          updateOffer(updatedOffer) // useEffect na changed zajisti zavolani saveOfferParts a nasledne rekalkulaci
        })
        .finally(() =>
          setTimeout(() => setLoading(false), 100)) // aby loading zmizel az po renderu       
    })
  }

  const itemPerGroupKey = Object.keys(insuranceGroups ?? {})
    .map(key => {
      const items = insuranceGroups[key]
        .filter(
          (x) =>
            ![
              Risk.RESPONSIBILITY,
              Risk.RESPONSIBILITY_REALESTATE,
              Risk.SPOL_PROPERTY,
              Risk.SPOL_FLOOD,
              Risk.SPOL_RESPONSIBILITY,
            ].includes(x.id),
        )

      return { items, key }
    })
    .filter(x => x.items.length > 0)

  return (
    <Box sx={{ display: 'grid', gap: 'px', alignSelf: 'end', w: '280px' }}>
      {itemPerGroupKey && itemPerGroupKey.length > 0 && itemPerGroupKey.map(x => {

        const items = x.items
        const headerCells = items.map(y => {
          return {
            id: y.id.toString(),
            title: y.name,
          }
        })
        const section = items[0]

        let icon: keyof typeof icons | undefined = undefined
        if (section.groupId)
          switch (section.groupId) {
            case PackageItemTypeCategory.Household: icon = 'household'; break
            case PackageItemTypeCategory.Realty: icon = 'property'; break
            default: icon = 'common'; break
          }

        return (
          <OfferPackageColumnSection
            key={section.groupId}
            id={section.groupId?.toString()}
            title={section.groupName}
            icon={icon}
            isOpened={openedColumns.includes(section.groupId)}
            onClick={() => onSectionTitleClick(section.groupId)}
            headerCells={headerCells}
            handleToggleAll={handleToggleAll}
            isContracted={isContracted}
          />
        )
      })}


      {responsibility &&
        <SectionTitle
          icon='lifeLiability'
          label={
            <Flex w={'100%'}>
              <Text sx={{ lineHeight: 5, fontWeight: 'medium', whiteSpace: 'nowrap' }}>{responsibility.name}</Text>

              <Checkbox
                size="sm"
                ml='auto'
                disabled={!responsibilityRiskItem?.addable || isContracted}
                isChecked={!!responsibilityRiskItem?.selected}
                onChange={e => handleToggleAll(!responsibilityRiskItem?.selected, Risk.RESPONSIBILITY)}
              />

            </Flex>
          }
        />
      }

      {responsibilityRealestate &&
        <SectionTitle
          icon='propertyLiability'
          label={
            <Flex w={'100%'}>
              <Text sx={{ lineHeight: 5, fontWeight: 'medium', whiteSpace: 'nowrap' }}>{responsibilityRealestate.name}</Text>

              <Checkbox
                size="sm"
                ml='auto'
                disabled={!responsibilityRealestateRiskItem?.addable || isContracted}
                isChecked={!!responsibilityRealestateRiskItem?.selected}
                onChange={e => handleToggleAll(!responsibilityRealestateRiskItem?.selected, Risk.RESPONSIBILITY_REALESTATE)}
              />

            </Flex>
          }
        // label={responsibilityRealestate.name} 
        />
      }

      {spolProperty &&
        <SectionTitle label={spolProperty.name} />
      }

      {spolFlood &&
        <SectionTitle label={spolFlood.name} />
      }

      {spolResponsibility &&
        <SectionTitle label={spolResponsibility.name} />
      }

      <SectionTitle label="Sleva" />

      <SectionTitle label="Splátka" />
    </Box>
  )
}
